import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { api } from '@orisini/ui/lib/api'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'

interface Device {
  id: string
  name: string
  tokenPrefix: string
  platform: string | null
  agentVersion: string | null
  hostname: string | null
  lastSeenAt: number | null
  createdAt: number
}

interface ExtToken {
  id: string
  product: string
  name: string
  tokenPrefix: string
  lastSeenAt: number | null
  createdAt: number
}

/**
 * Perangkat agent dan ekstensi terpasang.
 *
 * Perangkat bersifat LINTAS produk (satu instalasi Orisini Agent melayani
 * semuanya), sedangkan token ekstensi terikat satu produk — itu sebabnya
 * keduanya ditampilkan sebagai dua daftar terpisah, bukan satu.
 */
export default function DevicesPage() {
  const [devices, setDevices] = useState<Device[]>([])
  const [tokens, setTokens] = useState<ExtToken[]>([])
  const [loading, setLoading] = useState(true)

  const muat = useCallback(async () => {
    const [d, t] = await Promise.all([
      api.get<{ devices: Device[] }>('/devices'),
      api.get<{ tokens: ExtToken[] }>('/extension/tokens'),
    ])
    setDevices(d.devices)
    setTokens(t.tokens)
    setLoading(false)
  }, [])

  useEffect(() => {
    muat().catch((err) => {
      setLoading(false)
      toast.error(err instanceof Error ? err.message : "Couldn't load devices")
    })
  }, [muat])

  async function cabutPerangkat(id: string) {
    try {
      await api.delete(`/devices/${id}`)
      toast.success('Device revoked')
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't revoke device")
    }
  }

  async function cabutEkstensi(id: string) {
    try {
      await api.delete(`/extension/tokens/${id}`)
      toast.success('Extension token revoked')
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't revoke token")
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Devices</h1>
          <p className="text-sm text-muted-foreground">
            One Orisini Agent installation serves every product you're subscribed to.
          </p>
        </div>

        {devices.length === 0 && (
          <Card className="flex flex-wrap items-center gap-4 p-4">
            <p className="min-w-0 flex-1 text-sm text-muted-foreground">
              No devices yet. Install Orisini Agent on your computer, then sign in with this
              account once to pair it.
            </p>
            <Button asChild size="sm">
              <Link to="/download">Download Agent</Link>
            </Button>
          </Card>
        )}

        {devices.map((d) => (
          <Card key={d.id} className="flex items-center gap-4 p-4">
            <div className="min-w-0 flex-1">
              <p className="font-medium">{d.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {[d.platform, d.agentVersion && `v${d.agentVersion}`, d.tokenPrefix + '…']
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <span className="text-xs text-muted-foreground">
              {d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString('en-GB') : 'Never active'}
            </span>
            <Button variant="outline" size="sm" onClick={() => void cabutPerangkat(d.id)}>
              Revoke
            </Button>
          </Card>
        ))}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Chrome extension</h2>
          <p className="text-sm text-muted-foreground">
            Each product has its own extension, so its tokens are separate too.
          </p>
        </div>

        {tokens.length === 0 && (
          <p className="text-sm text-muted-foreground">No extensions paired yet.</p>
        )}

        {tokens.map((t) => (
          <Card key={t.id} className="flex items-center gap-4 p-4">
            <div className="min-w-0 flex-1">
              <p className="font-medium">{t.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {t.product} · {t.tokenPrefix}…
              </p>
            </div>
            <span className="text-xs text-muted-foreground">
              {t.lastSeenAt ? new Date(t.lastSeenAt).toLocaleString('en-GB') : 'Never active'}
            </span>
            <Button variant="outline" size="sm" onClick={() => void cabutEkstensi(t.id)}>
              Revoke
            </Button>
          </Card>
        ))}
      </section>
    </div>
  )
}
